/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import AssertionError from 'assertion-error';
import isString from 'lodash/isString';

import config from '../../config';

/**
 * @import { Url } from '../url'
 */

/**
 * Atributy perzistentního sezení s nastavením zařízení.
 *
 * Typy u volitelných položek předpokládají, že došlo k migraci po spuštění aplikace.
 * @typedef SessionAttrs
 * @property {string | null} buildId Identifikátor sestavení aplikace.
 * @property {ServerDeviceId['deviceId'] | null} deviceId Identifikátor zařízení na serveru.
 * @property {string | null} fingerprint Jedinečný otisk, který identifikuje zařízení.
 * Otisk se generuje pouze 1x a pak se uloží zde pro další použití.
 * Tím se vyhýbá možnosti, že by generátor vytvořil později jiný otisk.
 * To by se mohlo stát třeba při výměně hardware částí zařízení.
 * @property {string} id Identifikátor.
 * @property {ServerDeviceId['serverUrl'] | null} serverUrl URL adresa API serveru.
 * @property {string} storage Řetězec určující nastavení úložiště, do kterého se bude stahovat obsah.
 * @property {string | null} version Logická verze aplikace.
 */

/**
 * @typedef MaybeServerDeviceId
 * @property {Url | null} [serverUrl] URL adresa API serveru.
 * @property {string | null} [deviceId] Identifikátor zařízení na serveru.
 */

/**
 * Validní identifikátor zařízení v rámci serveru.
 *
 * Podmnožina ze {@link SessionAttrs}, kde položky
 * {@link SessionAttrs.serverUrl} a {@link SessionAttrs.deviceId} jsou zadané
 * (non-null) a neprázdné řetězce.
 * @typedef ServerDeviceId
 * @property {Url} serverUrl URL adresa API serveru.
 * @property {string} deviceId Identifikátor zařízení na serveru.
 */

/**
 * Prázdné (a tím pádem defaultní) SessionAttrs.
 *
 * Primární použití je pro migraci defaultizací při startu aplikace
 * a používá se i pro testy.
 * @type {SessionAttrs}
 * @readonly
 */
export const emptySessionAttrs = {
  buildId: null,
  deviceId: null,
  fingerprint: null,
  id: config.device.session.defaultId,
  serverUrl: null,
  storage: config.storage,
  version: null
};

/**
 * Zkontroluje, zda {@link maybeServerDeviceId} je {@link ServerDeviceId}.
 * @param {MaybeServerDeviceId} maybeServerDeviceId
 * @returns {maybeServerDeviceId is ServerDeviceId}
 */
export function isServerDeviceId(maybeServerDeviceId) {
  const { deviceId, serverUrl } = maybeServerDeviceId;
  return (
    isString(deviceId) &&
    deviceId.length > 0 &&
    isString(serverUrl) &&
    serverUrl.length > 0
  );
}

/**
 * Zkontroluje, zda {@link maybeServerDeviceId} je {@link ServerDeviceId}
 * a případně vyhodí chybu.
 * @param {MaybeServerDeviceId} maybeServerDeviceId
 * @returns {asserts maybeServerDeviceId is ServerDeviceId}
 * @throws {AssertionError}
 */
export function assertIsServerDeviceId(maybeServerDeviceId) {
  if (!isServerDeviceId(maybeServerDeviceId)) {
    const { deviceId, serverUrl } = maybeServerDeviceId;
    throw new AssertionError(
      'Missing or empty serverUrl and/or deviceId.',
      /** @satisfies {MaybeServerDeviceId} */ ({ deviceId, serverUrl })
    );
  }
}
